import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getSocietyProfile, updateSocietyProfile, clearSocietyProfile } from "../../../../redux/actions/SocietyPortal/SocietyProfileAction/SocietyProfileAction";
import axiosInstance from "../../../../axios/axiosInstance";
import apiEndpoints from "../../../../axios/api";
import { notify } from "../../../../utils/notification";
import ProfileCard from "./Component/ProfileCard";
import ProfileInformation from "./Component/ProfileInformation";
import AboutSection from "./Component/AboutSection";
import BasicInformation from "./Component/BasicInformation";
import AssociationInformation from "./Component/AssociationInformation";
import ContactInformation from "./Component/ContactInformation";
import AddressSection from "./Component/AddressSection";
import SpecificDetails from "./Component/SpecificDetails";
import PropertyDetails from "./Component/PropertyDetails";
import GeofenceSettings from "./Component/GeofenceSettings";
import { selectedSocietyStorer } from '../../../../utils/webStorer';

// Backend returns paths like "/uploads/society-logos/1/logo-xxx.jpg" — prepend the API base
// so the <img> resolves against the backend origin instead of the frontend's.
const resolveAssetUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    const base = (process.env.REACT_APP_NODE_MOBILE_BASE_URL || "").replace(/\/$/, "");
    const rel = path.startsWith("/") ? path : `/${path}`;
    return `${base}${rel}`;
};

const SocietyProfile = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { loading, updateLoading, societyProfileData } = useSelector(state => state.SocietyProfileReducers);

    const [editingSection, setEditingSection] = useState(null);
    const [editData, setEditData] = useState({});
    const [editAddresses, setEditAddresses] = useState([]);
    const [editContacts, setEditContacts] = useState([]);

    // States and Cities management at parent level
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Fetch states on component mount
    const fetchStates = useCallback(async () => {
        setLoadingStates(true);
        try {
            const response = await axiosInstance.get(apiEndpoints.CTRL_GET_STATES_API, {
                params: { countryId: 1 }
            });

            // Handle different response structures
            let statesData = [];
            if (response?.data?.success) {
                statesData = response.data.data?.rows || response.data.data || [];
            } else if (Array.isArray(response?.data?.data)) {
                statesData = response.data.data;
            } else if (Array.isArray(response?.data)) {
                statesData = response.data;
            }

            setStates(statesData);
        } catch (error) {
            console.error("Error fetching states:", error);
        }
        setLoadingStates(false);
    }, []);

    // Fetch cities for a given state
    const fetchCities = useCallback(async (stateId) => {
        if (!stateId) {
            setCities([]);
            return;
        }
        setLoadingCities(true);
        try {
            const response = await axiosInstance.get(apiEndpoints.CTRL_GET_CITIES_API, {
                params: { stateId }
            });

            // Handle different response structures
            let citiesData = [];
            if (response?.data?.success) {
                citiesData = response.data.data?.rows || response.data.data || [];
            } else if (Array.isArray(response?.data?.data)) {
                citiesData = response.data.data;
            } else if (Array.isArray(response?.data)) {
                citiesData = response.data;
            }

            setCities(citiesData);
        } catch (error) {
            console.error("Error fetching cities:", error);
        }
        setLoadingCities(false);
    }, []);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            dispatch(getSocietyProfile({ id: societyId }));
        }
        // Fetch states on mount
        fetchStates();

        return () => {
            dispatch(clearSocietyProfile());
        };
    }, [dispatch, societyId, fetchStates, refreshKey]);

    // Fetch cities when society data loads with a stateId
    useEffect(() => {
        const stateId = societyProfileData?.addresses?.[0]?.state_id;
        if (stateId) {
            fetchCities(stateId);
        }
    }, [societyProfileData, fetchCities]);

    // Get raw API data for building update payload
    const rawData = societyProfileData || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rawDetails = rawData.details || {};

    const societyData = useMemo(() => {
        if (!societyProfileData) return null;

        const data = societyProfileData;
        const details = data.details || {};

        return {
            profileInfo: {
                societyName: data.name || "",
                societyNameForSMS: details.society_name_sms || "",
                madeBy: data.made_by || "",
                brandName: details.brand_name || "",
                panNumber: details.pan_number || "",
                tanNumber: details.tan_number || "",
                payPin: details.pay_pin || "",
                communityCurrency: details.community_currency || "Rupees",
                // Root society_logo (multipart upload on PATCH /societies/:id)
                // wins over the legacy details.logo_url
                profileImage: resolveAssetUrl(data.society_logo || details.logo_url)
            },
            about: {
                description: details.description || "",
                clubTiming: details.club_timing || ""
            },
            basicInfo: {
                builderName: details.builder_name || "",
                complexType: data.type || "",
                constructionStatus: details.construction_status || ""
            },
            associationInfo: {
                associationName: data.name || "",
                gstRegNo: details.gst_number || "",
                brandName: details.brand_name || "",
                projectName: details.project_name || "",
                localAuthorityRegNo: details.registration_number || "",
                propertyTaxNumber: details.property_tax_number || ""
            },
            contacts: (data.contacts || [])
                .filter(contact => contact.is_active === 1)
                .map(contact => ({
                    contact_id: contact.contact_id,
                    contactType: contact.contact_type || "EMERGENCY",
                    contactPersonName: contact.contact_person_name || "",
                    designation: contact.designation || "",
                    phone: contact.phone || "",
                    mobile: contact.mobile || "",
                    email: contact.email || "",
                    website: contact.website || "",
                    is_primary: contact.is_primary || 0,
                    is_active: contact.is_active || 1
                })),
            addresses: (data.addresses || []).map(addr => ({
                address_id: addr.address_id,
                addressType: addr.address_type || "REGISTERED",
                addressLine1: addr.address_line1 || "",
                addressLine2: addr.address_line2 || "",
                landmark: addr.landmark || "",
                stateId: addr.state_id || "",
                stateName: addr.state?.name || "",
                cityId: addr.city_id || "",
                cityName: addr.city?.name || "",
                pincode: addr.pincode || "",
                country_id: addr.country_id || 1,
                is_primary: addr.is_primary || 0,
                is_active: addr.is_active || 1,
                state: addr.state,
                city: addr.city
            })),
            specificDetails: {
                isRegistered: details.is_registered ?? 0,
                registrationData: data.registration_number || "",
                registrationAuthority: data.registration_authority || "",
                dateOfRegistration: details.registration_date || ""
            },
            propertyDetails: {
                totalCarpetArea: details.total_carpet_area || "",
                totalWaterInlets: details.total_water_inlets || "",
                unitOfMeasurement: details.unit_of_measurement || ""
            }
        };
    }, [societyProfileData]);

    const handleEdit = useCallback((section) => {
        setEditingSection(section);
        if (section === 'addresses') {
            // Normalize addresses - ensure only first primary is marked as primary
            const addresses = societyData?.addresses || [];
            let foundPrimary = false;
            const normalizedAddresses = addresses.map(addr => {
                if (addr.is_primary === 1 && !foundPrimary) {
                    foundPrimary = true;
                    return addr;
                }
                return { ...addr, is_primary: foundPrimary ? 0 : addr.is_primary };
            });
            // If no primary found, set first one as primary
            if (!foundPrimary && normalizedAddresses.length > 0) {
                normalizedAddresses[0] = { ...normalizedAddresses[0], is_primary: 1 };
            }
            setEditAddresses(normalizedAddresses);
        } else if (section === 'contacts') {
            // Normalize contacts - ensure only first primary is marked as primary
            const contacts = societyData?.contacts || [];
            let foundPrimary = false;
            const normalizedContacts = contacts.map(contact => {
                if (contact.is_primary === 1 && !foundPrimary) {
                    foundPrimary = true;
                    return contact;
                }
                return { ...contact, is_primary: foundPrimary ? 0 : contact.is_primary };
            });
            // If no primary found, set first one as primary
            if (!foundPrimary && normalizedContacts.length > 0) {
                normalizedContacts[0] = { ...normalizedContacts[0], is_primary: 1 };
            }
            setEditContacts(normalizedContacts);
        } else {
            setEditData(societyData[section] || {});
        }
    }, [societyData]);

    const handleCancel = useCallback(() => {
        setEditingSection(null);
        setEditData({});
        setEditAddresses([]);
        setEditContacts([]);
    }, []);

    const handleFieldChange = useCallback((field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Address field change handler
    const handleAddressFieldChange = useCallback((index, field, value) => {
        setEditAddresses(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    // Add new address (max 3, with unique address types)
    const handleAddAddress = useCallback((addressType) => {
        setEditAddresses(prev => {
            // Max 3 addresses
            if (prev.length >= 3) return prev;

            return [
                ...prev,
                {
                    addressType: addressType || "CORRESPONDENCE",
                    addressLine1: "",
                    addressLine2: "",
                    landmark: "",
                    stateId: "",
                    stateName: "",
                    cityId: "",
                    cityName: "",
                    pincode: "",
                    country_id: 1,
                    is_primary: 0,
                    is_active: 1
                }
            ];
        });
    }, []);

    // Remove address
    const handleRemoveAddress = useCallback((index) => {
        setEditAddresses(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Set primary address (only one can be primary, toggle if already primary)
    const handleSetPrimaryAddress = useCallback((selectedIndex) => {
        setEditAddresses(prev => {
            const isCurrentlyPrimary = prev[selectedIndex]?.is_primary === 1;
            const newPrimaryValue = isCurrentlyPrimary ? 0 : 1;

            // Create new array - set all to 0 first, then set selected one
            const updated = [];
            for (let i = 0; i < prev.length; i++) {
                if (i === selectedIndex) {
                    updated.push({ ...prev[i], is_primary: newPrimaryValue });
                } else {
                    updated.push({ ...prev[i], is_primary: 0 });
                }
            }
            return updated;
        });
    }, []);

    // Contact field change handler
    const handleContactFieldChange = useCallback((index, field, value) => {
        setEditContacts(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    // Add new contact. Not capped — a society can list as many numbers as it
    // needs, and repeated contact types (several EMERGENCY numbers) are fine:
    // society_contacts has no unique key on (society_id, contact_type).
    const handleAddContact = useCallback((contactType) => {
        setEditContacts(prev => {
            return [
                ...prev,
                {
                    contactType: contactType || "EMERGENCY",
                    contactPersonName: "",
                    designation: "",
                    phone: "",
                    mobile: "",
                    email: "",
                    website: "",
                    is_primary: 0,
                    is_active: 1
                }
            ];
        });
    }, []);

    // Remove contact
    const handleRemoveContact = useCallback((index) => {
        setEditContacts(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Set primary contact (only one can be primary, toggle if already primary)
    const handleSetPrimaryContact = useCallback((selectedIndex) => {
        setEditContacts(prev => {
            const isCurrentlyPrimary = prev[selectedIndex]?.is_primary === 1;
            const newPrimaryValue = isCurrentlyPrimary ? 0 : 1;

            const updated = [];
            for (let i = 0; i < prev.length; i++) {
                if (i === selectedIndex) {
                    updated.push({ ...prev[i], is_primary: newPrimaryValue });
                } else {
                    updated.push({ ...prev[i], is_primary: 0 });
                }
            }
            return updated;
        });
    }, []);

    const handleSave = useCallback(() => {
        // Build complete payload with all data
        const getUpdatedSocietyData = () => {
            const currentData = societyData || {};

            // Merge edited section data with current data
            const merged = {
                profileInfo: editingSection === 'profileInfo' ? editData : currentData.profileInfo,
                about: editingSection === 'about' ? editData : currentData.about,
                basicInfo: editingSection === 'basicInfo' ? editData : currentData.basicInfo,
                associationInfo: editingSection === 'associationInfo' ? editData : currentData.associationInfo,
                contacts: editingSection === 'contacts' ? editContacts : currentData.contacts,
                addresses: editingSection === 'addresses' ? editAddresses : currentData.addresses,
                specificDetails: editingSection === 'specificDetails' ? editData : currentData.specificDetails,
                propertyDetails: editingSection === 'propertyDetails' ? editData : currentData.propertyDetails
            };

            return merged;
        };

        const data = getUpdatedSocietyData();

        // Build addresses payload
        const addressesPayload = (data.addresses || []).map((addr) => ({
            ...(addr.address_id && { address_id: addr.address_id }),
            address_type: addr.addressType || addr.address_type || "REGISTERED",
            address_line1: addr.addressLine1 || addr.address_line1 || "",
            address_line2: addr.addressLine2 || addr.address_line2 || "",
            landmark: addr.landmark || "",
            country_id: addr.country_id || 1,
            state_id: addr.stateId || addr.state_id || "",
            city_id: addr.cityId || addr.city_id || "",
            pincode: addr.pincode || "",
            latitude: addr.latitude || null,
            longitude: addr.longitude || null,
            is_primary: addr.is_primary === 1 ? 1 : 0,
            is_active: addr.is_active || 1
        }));

        // Build contacts payload
        const contactsPayload = (data.contacts || []).map((contact) => ({
            ...(contact.contact_id && { contact_id: contact.contact_id }),
            contact_type: contact.contactType || contact.contact_type || "PRIMARY",
            contact_person_name: contact.contactPersonName || contact.contact_person_name || "",
            designation: contact.designation || "",
            email: contact.email || "",
            phone: contact.phone || "",
            mobile: contact.mobile || "",
            fax: contact.fax || null,
            website: contact.website || "",
            is_primary: contact.is_primary === 1 ? 1 : 0,
            is_active: contact.is_active || 1
        }));

        const payload = {
            // Society table fields
            name: data.profileInfo?.societyName,
            type: data.basicInfo?.complexType,
            registration_number: data.specificDetails?.registrationData,
            registration_authority: data.specificDetails?.registrationAuthority,
            made_by: data.profileInfo?.madeBy ?? "",

            // Details object
            details: {
                ...(rawDetails.detail_id && { detail_id: rawDetails.detail_id }),
                society_name_sms: data.profileInfo?.societyNameForSMS,
                brand_name: data.profileInfo?.brandName || data.associationInfo?.brandName,
                pan_number: data.profileInfo?.panNumber,
                tan_number: data.profileInfo?.tanNumber,
                pay_pin: data.profileInfo?.payPin,
                community_currency: data.profileInfo?.communityCurrency,
                description: data.about?.description,
                club_timing: data.about?.clubTiming,
                builder_name: data.basicInfo?.builderName,
                construction_status: data.basicInfo?.constructionStatus,
                is_registered: data.specificDetails?.isRegistered,
                gst_number: data.associationInfo?.gstRegNo,
                project_name: data.associationInfo?.projectName,
                registration_number: data.associationInfo?.localAuthorityRegNo,
                registration_date: data.specificDetails?.dateOfRegistration,
                property_tax_number: data.associationInfo?.propertyTaxNumber,
                total_carpet_area: data.propertyDetails?.totalCarpetArea,
                total_water_inlets: data.propertyDetails?.totalWaterInlets,
                unit_of_measurement: data.propertyDetails?.unitOfMeasurement
            },

            // Addresses array
            addresses: addressesPayload,

            // Contacts array
            contacts: contactsPayload
        };

        dispatch(updateSocietyProfile({
            id: societyId,
            payload
        }));

        setEditingSection(null);
        setEditData({});
        setEditAddresses([]);
        setEditContacts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, societyId, editingSection, editData, editAddresses, editContacts, societyData, rawDetails]);

    const logoInputRef = useRef(null);
    const [logoUploading, setLogoUploading] = useState(false);

    const handleChangeImage = () => {
        logoInputRef.current?.click();
    };

    const handleLogoFileSelected = async (e) => {
        const file = e.target.files?.[0];
        // Reset so picking the same file again still fires onChange
        e.target.value = "";
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            notify("error", "Please select an image file");
            return;
        }
        // Backend accepts PNG/JPG/JPEG/WebP/SVG/GIF up to 5MB on society_logo
        if (file.size > 5 * 1024 * 1024) {
            notify("error", "Image must be smaller than 5 MB");
            return;
        }
        if (!societyId) {
            notify("error", "Society not selected");
            return;
        }

        // Multipart PATCH /societies/:id — the uploaded `society_logo` file is
        // stored on the society row (it wins over any society_logo URL string).
        const formData = new FormData();
        formData.append("society_logo", file);

        setLogoUploading(true);
        try {
            const url = apiEndpoints.UPDATE_SOCIETY_PROFILE_API.replace(":id", societyId);
            const res = await axiosInstance.patch(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res?.success) {
                notify("success", res.message || "Logo updated successfully");
                dispatch(getSocietyProfile({ id: societyId }));
            } else {
                notify("error", res?.message || "Failed to upload logo");
            }
        } catch (err) {
            notify("error", err?.response?.data?.message || "Failed to upload logo");
        } finally {
            setLogoUploading(false);
        }
    };

    if (loading) {
        return (
            <>
                <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                        <div className="tw-w-8 tw-h-8 tw-border-2 tw-border-amber-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                        <p className="tw-text-slate-500 tw-text-sm">Loading profile...</p>
                    </div>
                </div>
            </>
        );
    }

    if (!societyData) {
        return (
            <>
                <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center">
                        <Icon icon="solar:buildings-2-bold-duotone" className="tw-text-3xl tw-text-slate-300" />
                        <p className="tw-text-slate-600 tw-font-medium">No profile found</p>
                        <p className="tw-text-slate-400 tw-text-sm">Please check your society configuration</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <h1 className="tw-text-lg tw-font-semibold tw-text-slate-800">
                    Society Profile
                </h1>
            </div>

            <ProfileCard
                data={{
                    societyName: societyData.profileInfo.societyName,
                    brandName: societyData.profileInfo.brandName,
                    profileImage: societyData.profileInfo.profileImage,
                    city: societyData.addresses?.[0]?.cityName,
                    state: societyData.addresses?.[0]?.stateName
                }}
                onChangeImage={handleChangeImage}
                uploading={logoUploading}
            />
            <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoFileSelected}
                style={{ display: "none" }}
            />

            <ProfileInformation
                data={editingSection === 'profileInfo' ? editData : societyData.profileInfo}
                isEditing={editingSection === 'profileInfo'}
                isLoading={updateLoading}
                onEdit={() => handleEdit('profileInfo')}
                onSave={handleSave}
                onCancel={handleCancel}
                onChange={handleFieldChange}
            />

            <AboutSection
                data={editingSection === 'about' ? editData : societyData.about}
                isEditing={editingSection === 'about'}
                isLoading={updateLoading}
                onEdit={() => handleEdit('about')}
                onSave={handleSave}
                onCancel={handleCancel}
                onChange={handleFieldChange}
            />

            {/* Side-by-side: Basic Info & Association Info */}
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                <BasicInformation
                    data={editingSection === 'basicInfo' ? editData : societyData.basicInfo}
                    isEditing={editingSection === 'basicInfo'}
                    isLoading={updateLoading}
                    onEdit={() => handleEdit('basicInfo')}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onChange={handleFieldChange}
                />

                <AssociationInformation
                    data={editingSection === 'associationInfo' ? editData : societyData.associationInfo}
                    isEditing={editingSection === 'associationInfo'}
                    isLoading={updateLoading}
                    onEdit={() => handleEdit('associationInfo')}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onChange={handleFieldChange}
                />
            </div>

            <ContactInformation
                contacts={editingSection === 'contacts' ? editContacts : societyData.contacts}
                isEditing={editingSection === 'contacts'}
                isLoading={updateLoading}
                onEdit={() => handleEdit('contacts')}
                onSave={handleSave}
                onCancel={handleCancel}
                onChange={handleContactFieldChange}
                onAddContact={handleAddContact}
                onRemoveContact={handleRemoveContact}
                onSetPrimary={handleSetPrimaryContact}
            />

            <AddressSection
                addresses={editingSection === 'addresses' ? editAddresses : societyData.addresses}
                isEditing={editingSection === 'addresses'}
                isLoading={updateLoading}
                onEdit={() => handleEdit('addresses')}
                onSave={handleSave}
                onCancel={handleCancel}
                onChange={handleAddressFieldChange}
                states={states}
                cities={cities}
                loadingStates={loadingStates}
                loadingCities={loadingCities}
                onFetchCities={fetchCities}
                onAddAddress={handleAddAddress}
                onRemoveAddress={handleRemoveAddress}
                onSetPrimary={handleSetPrimaryAddress}
            />

            {/* Side-by-side: Registration & Property Details */}
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                <SpecificDetails
                    data={editingSection === 'specificDetails' ? editData : societyData.specificDetails}
                    isEditing={editingSection === 'specificDetails'}
                    isLoading={updateLoading}
                    onEdit={() => handleEdit('specificDetails')}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onChange={handleFieldChange}
                />

                <PropertyDetails
                    data={editingSection === 'propertyDetails' ? editData : societyData.propertyDetails}
                    isEditing={editingSection === 'propertyDetails'}
                    isLoading={updateLoading}
                    onEdit={() => handleEdit('propertyDetails')}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onChange={handleFieldChange}
                />
            </div>

            {/* Attendance geofence — own endpoint (PATCH /societies/:id/geofence),
                so it manages its own edit state instead of the shared handleSave */}
            <GeofenceSettings
                societyId={societyId}
                data={rawData}
                onSaved={() => dispatch(getSocietyProfile({ id: societyId }))}
            />
        </>
    );
};

export default SocietyProfile;
